import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { NursingHome } from "@/hooks/use-nursing-home";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Eye, Users, TrendingUp, Calendar } from "lucide-react";
import { format, subDays, startOfDay, endOfDay } from "date-fns";

type ContextType = { nursingHome: NursingHome };

interface DailyViews {
  date: string;
  count: number;
}

export default function AnalyticsPage() {
  const { nursingHome } = useOutletContext<ContextType>();
  const [totalViews, setTotalViews] = useState(0);
  const [weeklyViews, setWeeklyViews] = useState(0);
  const [monthlyViews, setMonthlyViews] = useState(0);
  const [leadCount, setLeadCount] = useState(0);
  const [dailyViews, setDailyViews] = useState<DailyViews[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (nursingHome?.nh_slug) {
      fetchAnalytics();
    }
  }, [nursingHome?.nh_slug]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const now = new Date();
      const weekAgo = subDays(now, 7);
      const monthAgo = subDays(now, 30);

      // Fetch total views
      const { count: total } = await supabase
        .from("nursing_home_views")
        .select("*", { count: "exact", head: true })
        .eq("nursing_home_id", nursingHome.nh_slug);

      // Fetch weekly views
      const { count: weekly } = await supabase
        .from("nursing_home_views")
        .select("*", { count: "exact", head: true })
        .eq("nursing_home_id", nursingHome.nh_slug)
        .gte("viewed_at", weekAgo.toISOString());

      // Fetch monthly views
      const { count: monthly } = await supabase
        .from("nursing_home_views")
        .select("*", { count: "exact", head: true })
        .eq("nursing_home_id", nursingHome.nh_slug)
        .gte("viewed_at", monthAgo.toISOString());

      // Fetch lead count
      const { count: leads } = await supabase
        .from("nursing_home_leads")
        .select("*", { count: "exact", head: true })
        .eq("nursing_home_id", nursingHome.nh_slug);

      // Fetch daily views for the last 14 days
      const twoWeeksAgo = subDays(now, 14);
      const { data: viewsData } = await supabase
        .from("nursing_home_views")
        .select("viewed_at")
        .eq("nursing_home_id", nursingHome.nh_slug)
        .gte("viewed_at", twoWeeksAgo.toISOString())
        .order("viewed_at", { ascending: true });

      // Group by day
      const dailyMap = new Map<string, number>();
      for (let i = 0; i < 14; i++) {
        const day = format(subDays(now, 13 - i), "MMM dd");
        dailyMap.set(day, 0);
      }
      viewsData?.forEach((v) => {
        const day = format(new Date(v.viewed_at), "MMM dd");
        dailyMap.set(day, (dailyMap.get(day) || 0) + 1);
      });
      
      setDailyViews(Array.from(dailyMap.entries()).map(([date, count]) => ({ date, count })));
      setTotalViews(total || 0);
      setWeeklyViews(weekly || 0);
      setMonthlyViews(monthly || 0);
      setLeadCount(leads || 0);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const conversionRate = totalViews > 0 ? ((leadCount / totalViews) * 100).toFixed(1) : "0.0";
  const maxDailyCount = Math.max(...dailyViews.map(d => d.count), 1);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Analytics</h1>
        <p className="text-muted-foreground">See how your profile is performing</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Eye className="h-4 w-4 text-muted-foreground" />
              This Week
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{weeklyViews}</div>
            <p className="text-xs text-muted-foreground">profile views</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              This Month
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{monthlyViews}</div>
            <p className="text-xs text-muted-foreground">profile views</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              Total Leads
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{leadCount}</div>
            <p className="text-xs text-muted-foreground">enquiries received</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              Conversion
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{conversionRate}%</div>
            <p className="text-xs text-muted-foreground">views → enquiries</p>
          </CardContent>
        </Card>
      </div>

      {/* Daily Views Chart (simple bar chart) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Daily Profile Views (Last 14 Days)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-1 h-40">
            {dailyViews.map((day) => (
              <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-xs text-muted-foreground">{day.count}</span>
                <div
                  className="w-full bg-primary/80 rounded-t-sm min-h-[2px] transition-all"
                  style={{ height: `${(day.count / maxDailyCount) * 100}%` }}
                />
                <span className="text-[10px] text-muted-foreground rotate-[-45deg] origin-top-left whitespace-nowrap mt-1">
                  {day.date}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* All-time */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">All-Time</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-3xl font-bold">{totalViews}</p>
          <p className="text-sm text-muted-foreground">total profile views since tracking began</p>
        </CardContent>
      </Card>
    </div>
  );
}
